import { useState, useRef } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export function CreateIssueModal({ open, onOpenChange, onCreated }) {
  const { patchUser, user } = useAuth();
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const reset = () => {
    setCategory(""); setTitle(""); setDescription(""); setLocation("");
    setFile(null); setPreview(null);
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!category) return toast.error("Choose an issue category");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("title", title);
      fd.append("description", description);
      fd.append("location", location);
      if (file) fd.append("file", file);
      const { data } = await api.post("/issues", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patchUser({ coins: (user?.coins || 0) + 3 });
      toast.success("Issue posted! +3 Dimagi coins earned.");
      onCreated?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not post issue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="create-issue-modal" className="sm:max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-display">Raise a local issue</DialogTitle>
          <p className="text-xs text-muted-foreground">Focus on the problem, not the party. +3 Dimagi coins for every issue you raise.</p>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="space-y-1.5">
            <Label>Category</Label>
            <div data-testid="issue-category-picker" className="grid grid-cols-3 gap-2">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = category === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`category-pick-${c.key}`}
                    onClick={() => setCategory(c.key)}
                    className={`flex flex-col items-center gap-1.5 rounded-lg border p-2.5 text-xs font-medium transition-all ${
                      active ? "border-transparent text-white" : "border-border text-muted-foreground hover:border-foreground/30"
                    }`}
                    style={active ? { background: c.color } : {}}
                  >
                    <Icon size={18} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input data-testid="issue-title-input" required maxLength={140} value={title}
              onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Broken road near Anna Nagar bus stop" />
          </div>

          <div className="space-y-1.5">
            <Label>Describe the issue</Label>
            <Textarea data-testid="issue-description-input" required rows={4} maxLength={2000} value={description}
              onChange={(e) => setDescription(e.target.value)} placeholder="What's the problem? Who is affected? What should be done?" />
          </div>

          <div className="space-y-1.5">
            <Label>Location <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input data-testid="issue-location-input" maxLength={120} value={location}
              onChange={(e) => setLocation(e.target.value)} placeholder="Ward / area / city" />
          </div>

          <div className="space-y-1.5">
            <Label>Photo <span className="text-muted-foreground font-normal">(optional)</span></Label>
            {preview ? (
              <div className="relative rounded-lg overflow-hidden border border-border">
                <img src={preview} alt="preview" className="w-full max-h-52 object-cover" />
                <button type="button" onClick={() => { setFile(null); setPreview(null); }}
                  className="absolute top-2 right-2 grid place-items-center h-8 w-8 rounded-full bg-black/60 text-white">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button type="button" data-testid="issue-photo-button" onClick={() => fileRef.current?.click()}
                className="flex items-center justify-center gap-2 w-full rounded-lg border border-dashed border-border py-6 text-sm text-muted-foreground hover:border-saffron/50 hover:text-saffron transition-colors">
                <ImagePlus size={18} /> Add a photo of the issue
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickFile} />
          </div>

          <Button data-testid="issue-submit-button" type="submit" disabled={loading}
            className="w-full bg-saffron text-primary-foreground hover:bg-saffron/90 font-semibold">
            {loading ? <Loader2 className="animate-spin" size={16} /> : "Post issue"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
